
import { Alert, Button, Dialog, Flex, ProviderConsumer as FluentUIThemeConsumer, Loader, Text } from "@fluentui/react-northstar";
import { Input } from 'adaptivecards';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import EmptySVG from '../../../../svg/empty.svg';
import AppCSS from '../../../App.module.css';
import { appState, useSetState } from "../../../AppState";
import { formatInputRawData, mapCardInputToAnswers } from '../TicketTemplateAnswer';
import { CustomFormAttachmentsProps } from '../../../templates/CustomForms/CustomForm.Interface';
import { updateIsCustomFormSavingState } from '../../../templates/CustomForms/CustomForm.Service';
import { EditTicketEvent } from '../../../tikit/ticketHelper';
import { platformService } from '../../services/platform.service';
import { uploadService } from "../../services/upload.service";
import { toastDefault } from '../../utils/constants';
import { canParseJson, parseJsonRecovering } from '../../utils/jsonRecovery';
import { startFileUploadSession } from '../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { EmptyData } from '../EmptyData';
import { TicketCustomFormAdaptiveCard } from "./components/TicketCustomFormAdaptiveCard";
import { isUpdateAnswersDisabled } from "./updateAnswersButton";

interface TicketCustomFormSectionProps {
  customForms: any;
  showUpdateAnswerButton?: boolean;
  ticketId?: number;
  setAppStateEvent?: any;
  dispatchEvent?: any;
  disabledFormInputs?: boolean;
  onInteraction?: any;
  isEndUserPage?: boolean;
  template?: any;
  showSummaryInfo?: boolean;
}

export const TicketCustomFormSection = (Props: TicketCustomFormSectionProps) => {
  const { customForms, showUpdateAnswerButton, ticketId, setAppStateEvent, dispatchEvent, disabledFormInputs, onInteraction, isEndUserPage, template, showSummaryInfo } = Props;
  const [loading, setLoading] = useState(false);
  const [updatedCardAnswerJson, setUpdatedCardAnswerJson] = useState({});
  const [templateFormCount, setTemplateFormCount] = useState(0);
  const [templateFormCountLoaded, setTemplateFormCountLoaded] = useState(false);
  const [disableForm, setDisabledForm] = useState(false);
  const [updatedCustomForms, setUpdatedCustomForms] = useState(customForms);
  const [sortedTemplateForms, setSortedTemplateForms] = useState(customForms);
  const [updatedFiles, setUpdatedFiles] = useState<CustomFormAttachmentsProps[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [showSummaryHeader, setShowSummaryHeader] = useState(false);
  const [showFormName, setShowFormName] = useState(false);
  const [summaryHeaderText, setSummaryHeaderText] = useState("");

  const { t } = useTranslation();
  const api = new platformService();
  const uploadHelper = new uploadService();
  const currentState = appState();
  const setCurrentState = useSetState();

  const unreadableForms = useMemo(
    () => (customForms ?? []).filter(form => {
      const answers = form?.CardAnswerJson;
      return typeof answers === 'string' && answers.trim().length > 0 && !canParseJson(answers);
    }),
    [customForms]
  );

  const onUpdateAnswerClick = async () => {
    if (templateFormCount > 1) {
      setAppStateEvent(preState =>
      ({
        ...preState,
        currentCustomFormIndex: 0,
        openSendFormDialogFrom: "updateAnswer",
        answerCustomFormNow: true
      }));
      dispatchEvent({ type: 'send-template-form' });
    } else if (updatedCustomForms.some(cf => (cf.CardAnswerJson ?? '').includes("Ticket.")))
      setOpenConfirmDialog(true);
    else
      await confirmUpdateAnswers();
  }

  const confirmUpdateAnswers = async () => {
    // Saving would replace the stored answers with whatever the blank fields
    // hold, so refuse while any of them cannot be read back.
    if (unreadableForms.length > 0) {
      setOpenConfirmDialog(false);
      return;
    }

    updateIsCustomFormSavingState(true, setCurrentState);
    updatedCustomForms.forEach(form => {
      let cardFormAnswersObj = parseJsonRecovering(form.CardAnswerJson);
      formatInputRawData(parseJsonRecovering(form.CardTemplateJson)?.body, cardFormAnswersObj);
      form.CardAnswerJson = JSON.stringify(cardFormAnswersObj);
    });
    await api.updateCustomForms(ticketId, updatedCustomForms);
    if (updatedFiles.length > 0) {
      let updatedFormFiles: any[] = [];
      for (const formFile of updatedFiles) {
        const filesToAttach = await startFileUploadSession(api, uploadHelper, formFile.Files, ticketId, false, "");
        updatedFormFiles.push({
          TemplateFormId: formFile.TemplateFormId,
          Files: filesToAttach,
          Message: formFile.Message,
          IsPrivate: formFile.IsPrivate
        });
      }

      await api.attachWebCustomFormFiles(ticketId, customForms, updatedFormFiles, false, true);
    }

    if (onInteraction)
      onInteraction({ event: EditTicketEvent.CustomFormUpdated, data: { hasUploaded: updatedFiles.length > 0 } });
    toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.success-response'), toastDefault);
    setOpenConfirmDialog(false);
    setIsFormModified(false)
  }

  const onInputValueChanged = (input: Input) => {
    let thisUpdatedCardAnswerJson = updatedCardAnswerJson;
    mapCardInputToAnswers(input, thisUpdatedCardAnswerJson);
    setUpdatedCardAnswerJson(thisUpdatedCardAnswerJson);
    if (!updateCustomForm(thisUpdatedCardAnswerJson))
      return;
    setIsFormModified(true);
  };

  // Leaves the form unmodified when the stored answers cannot be read, so an
  // edit on top of blank fields can never be saved over them.
  const updateCustomForm = (newAnswer: any): boolean => {
    try {
      const updatedItems = customForms.map(item => {
        const cardAnswer = item.CardAnswerJson;
        const cardAnswerJson = parseJsonRecovering((cardAnswer?.length > 0 ? cardAnswer : "{}"));
        const assignedValues = Object.assign(cardAnswerJson, newAnswer);
        return item.Order === 0 ? { ...item, CardAnswerJson: JSON.stringify(assignedValues) } : item
      }
      );
      setUpdatedCustomForms(updatedItems);
      return true;
    } catch (e) {
      console.error('Stored custom form answers could not be read; edit discarded.', e);
      return false;
    }
  };

  useEffect(() => {
    setLoading(currentState.customFormState.isCustomFormSaving)
  }, [currentState.customFormState.isCustomFormSaving])

  useEffect(() => {
    const sortedForms = [...customForms].sort((a, b) => a.Order - b.Order);
    setSortedTemplateForms(sortedForms);
  }, [customForms])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isNaN(ticketId)) return;

        const { data } = await api.getTicket(ticketId, '?$expand=CustomFormTemplate($expand=TemplateForms)');

        // If `data.CustomFormTemplate` exists use it, otherwise fallback
        if (data.CustomFormTemplate !== null) {
          setTemplateFormCount(data.CustomFormTemplate?.TemplateForms?.length ?? 0);
        } else {
          setTemplateFormCount(template?.TemplateForms?.length);
        }
      } catch (error) {
        console.error("Failed to fetch ticket:", error);
      } finally {
        // Reached on the isNaN return as well, so a caller with no ticket - the summary pages - never waits.
        setTemplateFormCountLoaded(true);
      }
    };

    fetchData();
    setShowSummaryHeader(showSummaryInfo);
    setShowFormName(template?.ShowFormNameOnSummary);
    setSummaryHeaderText(template?.SummaryHeaderText);
  }, []);

  useEffect(() => {
    setDisabledForm(disabledFormInputs || templateFormCount > 1);
  }, [templateFormCount])

  const renderEmptyResult = () => {
    if (customForms.length !== 0) {
      return null;
    }

    return (
      <Flex.Item>
        <EmptyData
          headerText={t("ticket-details.ticket-conversation.empty-result")}
          subheaderText=""
          SVGIcon={<EmptySVG width={100} height={100} className="mb-2" />}
        />
      </Flex.Item>
    )
  }

  const renderUnreadableAnswersAlert = () => {
    if (unreadableForms.length === 0) {
      return null;
    }

    return (
      <Alert
        warning
        visible
        className={`mb-4`}
        content={t('ticket-details.edit-ticket-form.custom-form-answer.answers-unreadable')}
      />
    )
  }

  const renderSummaryHeader = () => {
    if (!showSummaryHeader) {
      return null;
    }

    return (
      <Text content={summaryHeaderText} className="mr-2 mb-5" size="large" ></Text>
    )
  }

  const renderFormName = (customForm) => {
    if (!showFormName) {
      return null;
    }
    return (
      <Text
        content={customForm.Name}
        className="mr-2 mt-10"
        weight='semibold'
        size="medium">
      </Text>
    )
  }

  const renderShowUpdateAnswerButton = () => {
    if (!showUpdateAnswerButton) {
      return null;
    }

    return (
      <Button
        className={'mb-4 mt-2'}
        content={t('ticket-details.edit-ticket-form.custom-form-answer.button.update-answer')}
        primary
        onClick={onUpdateAnswerClick}
        disabled={isUpdateAnswersDisabled(isFormModified, templateFormCount)}>
      </Button>
    )
  }

  if (loading || !templateFormCountLoaded) {
    return (
      <FluentUIThemeConsumer
        render={globalTheme => (
          <Flex.Item>
            <div className={`pt-5 mt-5`}>
              <Loader />
            </div>
          </Flex.Item>
        )}
      ></FluentUIThemeConsumer>
    )
  }

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <>
          {renderEmptyResult()}
          <Flex.Item styles={{ flexGrow: 1 }}>
            <div className={`print-hidden overflow-auto h-sm:m-h-1/4 h-md:m-h-2/5`}>
              <div>
                {renderUnreadableAnswersAlert()}
                {renderSummaryHeader()}
                {sortedTemplateForms.map((customForm, index) => {
                  return (
                    <>
                      <Flex column gap="gap.small">
                        {renderFormName(customForm)}
                        <TicketCustomFormAdaptiveCard
                          key={`customForm-${customForm?.TemplateFormId ?? 0}`}
                          customForm={customForm}
                          disableForm={disableForm}
                          onInputValueChanged={onInputValueChanged}
                          currentState={currentState}
                          isEndUserPage={isEndUserPage}
                          templateFormCount={templateFormCount}
                          dispatchEvent={files => {
                            setUpdatedFiles(files);
                            setIsFormModified(true);
                          }} />
                      </Flex>
                    </>
                  )
                })
                }
                {renderShowUpdateAnswerButton()}
                <Dialog
                  className={`${AppCSS['confirmDialog']}`}
                  cancelButton={t('knowledge.add-knowledge-modal.button.cancel')}
                  confirmButton={t('common.buttons.confirm')}
                  open={openConfirmDialog}
                  onConfirm={() => {
                    confirmUpdateAnswers();
                  }}
                  onCancel={() => {
                    setOpenConfirmDialog(false);
                  }}
                  header={t('ticket-details.edit-ticket-form.custom-form-answer.button.update-answer')}
                  content={t('ticket-details.edit-ticket-form.custom-form-answer.update-answer-hint')}
                  style={{ maxWidth: '500px' }}
                />
              </div>
            </div>
          </Flex.Item>
        </>
      )} />
  );
};
